import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ProductSelector = ({ onAdd, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Mock product data
  const products = [
    {
      id: 1,
      code: 'LAP-001',
      name: 'Laptop Dell XPS 13',
      category: 'Informática',
      price: 1299.99,
      stock: 15,
      description: 'Laptop ultrabook con procesador Intel i7, 16GB RAM, 512GB SSD'
    },
    {
      id: 2,
      code: 'MON-002',
      name: 'Monitor Samsung 27" 4K',
      category: 'Informática',
      price: 399.99,
      stock: 8,
      description: 'Monitor 4K UHD de 27 pulgadas con tecnología HDR'
    },
    {
      id: 3,
      code: 'TEC-003',
      name: 'Teclado Mecánico Logitech',
      category: 'Accesorios',
      price: 129.99,
      stock: 25,
      description: 'Teclado mecánico gaming con switches Cherry MX'
    },
    {
      id: 4,
      code: 'IMP-004',
      name: 'Impresora HP LaserJet Pro',
      category: 'Oficina',
      price: 249.99,
      stock: 5,
      description: 'Impresora láser monocromática para oficina'
    },
    {
      id: 5,
      code: 'CAB-005',
      name: 'Cable HDMI 2.1 Premium',
      category: 'Accesorios',
      price: 29.99,
      stock: 50,
      description: 'Cable HDMI 2.1 de alta velocidad, 2 metros'
    },
    {
      id: 6,
      code: 'SIL-006',
      name: 'Silla Ergonómica Oficina',
      category: 'Mobiliario',
      price: 199.99,
      stock: 12,
      description: 'Silla ergonómica con soporte lumbar ajustable'
    }
  ];

  const categories = ['all', ...new Set(products.map(p => p.category))];

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setQuantity(1);
  };

  const handleAddProduct = () => {
    if (selectedProduct && quantity > 0) {
      onAdd(selectedProduct, quantity);
    }
  };

  const getStockStatus = (stock) => {
    if (stock === 0) return { label: 'Sin Stock', color: 'text-error' };
    if (stock <= 5) return { label: 'Stock Bajo', color: 'text-warning' };
    return { label: 'Disponible', color: 'text-success' };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4">
      <div className="bg-surface rounded-lg shadow-modal border border-border w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-heading font-semibold text-text-primary">
              Seleccionar Producto
            </h3>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onClose}
            />
          </div>
        </div>

        {/* Search and Filters */}
        <div className="p-4 border-b border-border">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                type="search"
                placeholder="Buscar por nombre o código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="sm:w-48">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              >
                <option value="all">Todas las categorías</option>
                {categories.filter(cat => cat !== 'all').map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Product List */}
          <div className="flex-1 overflow-y-auto p-4">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="Package" size={48} className="text-text-muted mx-auto mb-4" />
                <p className="text-text-muted">
                  No se encontraron productos
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredProducts.map((product) => {
                  const stockStatus = getStockStatus(product.stock);
                  const isSelected = selectedProduct?.id === product.id;
                  
                  return (
                    <div
                      key={product.id}
                      onClick={() => handleProductSelect(product)}
                      className={`p-4 rounded-lg border cursor-pointer transition-micro ${
                        isSelected
                          ? 'border-primary bg-primary-50' :'border-border hover:border-primary-200 hover:bg-secondary-50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-text-primary truncate">
                            {product.name}
                          </h4>
                          <p className="text-sm text-text-muted">
                            Código: {product.code}
                          </p>
                        </div>
                        <div className="ml-2 text-right">
                          <div className="font-semibold text-primary">
                            {formatCurrency(product.price)}
                          </div>
                          <div className={`text-xs ${stockStatus.color}`}>
                            {stockStatus.label}
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-sm text-text-secondary mb-3 line-clamp-2">
                        {product.description}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-text-muted">
                          Categoría: {product.category}
                        </span>
                        <span className="text-sm text-text-secondary">
                          Stock: {product.stock}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected Product Details */}
          {selectedProduct && (
            <div className="w-80 border-l border-border p-4 bg-secondary-50">
              <h4 className="font-medium text-text-primary mb-4">
                Producto Seleccionado
              </h4>
              
              <div className="space-y-4">
                <div>
                  <h5 className="font-medium text-text-primary">
                    {selectedProduct.name}
                  </h5>
                  <p className="text-sm text-text-muted">
                    Código: {selectedProduct.code}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-text-secondary">
                    {selectedProduct.description}
                  </p>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm text-text-secondary">Precio:</span>
                  <span className="font-semibold text-primary">
                    {formatCurrency(selectedProduct.price)}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm text-text-secondary">Stock:</span>
                  <span className={`text-sm ${getStockStatus(selectedProduct.stock).color}`}>
                    {selectedProduct.stock} unidades
                  </span>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Cantidad
                  </label>
                  <Input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                    min="1"
                    max={selectedProduct.stock}
                  />
                </div>
                
                <div className="pt-2 border-t border-border">
                  <div className="flex justify-between font-semibold">
                    <span className="text-text-primary">Total:</span>
                    <span className="text-primary">
                      {formatCurrency(selectedProduct.price * quantity)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-secondary-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-text-muted">
              {selectedProduct ? 'Producto seleccionado' : 'Seleccione un producto'}
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={onClose}
              >
                Cancelar
              </Button>
              
              <Button
                variant="primary"
                onClick={handleAddProduct}
                disabled={!selectedProduct || quantity <= 0 || quantity > selectedProduct.stock}
                iconName="Plus"
              >
                Agregar Producto
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSelector;